import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter
} from '../components/ui/dialog';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { getFaqs, createFaq, updateFaq, deleteFaq } from '../lib/api';

const emptyFaq = {
  question: '',
  question_es: '',
  answer: '',
  answer_es: '',
  order: 0,
  active: true
};

export default function AdminFaqs() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingFaq, setEditingFaq] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [formData, setFormData] = useState(emptyFaq);
  const [expandedFaq, setExpandedFaq] = useState(null);

  const fetchFaqs = async () => {
    try {
      const response = await getFaqs();
      setFaqs(response.data);
    } catch (error) {
      toast.error('Error loading FAQs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFaqs();
  }, []);

  const openCreate = () => {
    setFormData({ ...emptyFaq, order: faqs.length });
    setEditingFaq('new');
  };

  const openEdit = (faq) => {
    setFormData(faq);
    setEditingFaq(faq.id);
  };

  const handleSave = async () => {
    try {
      if (editingFaq === 'new') {
        await createFaq(formData);
        toast.success('FAQ created');
      } else {
        await updateFaq(editingFaq, formData);
        toast.success('FAQ updated');
      }
      setEditingFaq(null);
      fetchFaqs();
    } catch (error) {
      toast.error('Error saving FAQ');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteFaq(deleteId);
      toast.success('FAQ deleted');
      setDeleteId(null);
      fetchFaqs();
    } catch (error) {
      toast.error('Error deleting FAQ');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-slate-400">Loading FAQs...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-faqs">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-white lg:hidden">FAQs</h1>
        <Button 
          onClick={openCreate}
          className="bg-gradient-to-r from-orange-600 to-yellow-500 text-black font-bold"
          data-testid="add-faq-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add FAQ
        </Button>
      </div>

      {/* FAQs List */}
      <div className="space-y-4">
        {faqs.map((faq) => (
          <div 
            key={faq.id} 
            className={`admin-card ${!faq.active && 'opacity-60'}`}
            data-testid={`faq-card-${faq.id}`}
          >
            <button
              className="w-full p-4 flex items-center justify-between"
              onClick={() => setExpandedFaq(expandedFaq === faq.id ? null : faq.id)}
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-cyan-500/20 flex items-center justify-center text-cyan-400 font-bold text-sm">
                  {faq.order + 1}
                </div>
                <span className="font-semibold text-white text-left">{faq.question}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-1 rounded-full ${
                  faq.active 
                    ? 'bg-green-500/20 text-green-400' 
                    : 'bg-red-500/20 text-red-400'
                }`}>
                  {faq.active ? 'Active' : 'Inactive'}
                </span>
                {expandedFaq === faq.id ? (
                  <ChevronUp className="w-5 h-5 text-slate-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-slate-400" />
                )}
              </div>
            </button>

            {expandedFaq === faq.id && (
              <div className="px-4 pb-4">
                <p className="text-slate-400 mb-4 pl-11">{faq.answer}</p>
                <div className="flex gap-2 pl-11">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-white/10 text-white hover:bg-white/5"
                    onClick={() => openEdit(faq)}
                  >
                    <Pencil className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                    onClick={() => setDeleteId(faq.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {faqs.length === 0 && (
        <div className="admin-card p-12 text-center">
          <HelpCircle className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400">No FAQs found</p>
        </div>
      )}

      {/* Edit/Create Dialog */}
      <Dialog open={!!editingFaq} onOpenChange={() => setEditingFaq(null)}>
        <DialogContent className="bg-[#1E293B] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingFaq === 'new' ? 'Add New FAQ' : 'Edit FAQ'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <label className="text-sm text-slate-400">Question (EN)</label>
              <Input
                value={formData.question}
                onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
                placeholder="How do I book a transfer?"
                data-testid="faq-question-input"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm text-slate-400">Question (ES)</label>
              <Input
                value={formData.question_es || ''}
                onChange={(e) => setFormData({ ...formData, question_es: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm text-slate-400">Answer (EN)</label>
              <Textarea
                value={formData.answer}
                onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                className="bg-white/5 border-white/10 text-white min-h-[100px]"
                data-testid="faq-answer-input"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm text-slate-400">Answer (ES)</label>
              <Textarea
                value={formData.answer_es || ''}
                onChange={(e) => setFormData({ ...formData, answer_es: e.target.value })}
                className="bg-white/5 border-white/10 text-white min-h-[100px]"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm text-slate-400">Order</label>
              <Input
                type="number"
                min="0"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                className="bg-white/5 border-white/10 text-white"
              />
            </div>

            <div className="flex items-center justify-between">
              <label className="text-sm text-slate-400">Active</label>
              <Switch
                checked={formData.active}
                onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditingFaq(null)}
              className="border-white/10 text-white hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              className="bg-cyan-600 hover:bg-cyan-700"
              data-testid="save-faq-btn"
            >
              Save FAQ
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-[#1E293B] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete FAQ?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
